import { useCallback, useState } from "react";
import { toast } from "sonner";
import { useGlobalLoading } from "../components/loading/global-loading-context";
import type { UserDetails } from "../contexts/user-data-context";
import { useUserData } from "../contexts/user-data-context";
import { useAnalytics } from "../lib/analytics";
import { useClientTranslations } from "../lib/i18n/client";
import { saveProfileFields } from "../services/profileService";

export interface ModalAnswers {
	"name-location": Record<string, unknown>;
	"birth-year-gender": Record<string, unknown>;
	"current-status": Record<string, unknown>;
	"fertility-role": Record<string, unknown>;
	"journey-type": Record<string, unknown>;
	"additional-medical-info": Record<string, unknown>;
	clinic: Record<string, unknown>;
	"change-password": Record<string, unknown>;
}

export function useB2CProfileModals() {
	const { userData, updateUserData } = useUserData();
	const { setLoading } = useGlobalLoading();
	const { t } = useClientTranslations("profile");
	const { t: tCommon } = useClientTranslations("common");
	const { logEvent } = useAnalytics();

	// Modal states
	const [openModalId, setOpenModalId] = useState<string | null>(null);

	// Answer states for all modals
	const [modalAnswers, setModalAnswers] = useState<ModalAnswers>({
		"name-location": {},
		"birth-year-gender": {},
		"current-status": {},
		"fertility-role": {},
		"journey-type": {},
		"additional-medical-info": {},
		clinic: {},
		"change-password": {},
	});

	// Initialize answers from user data
	const initializeAnswers = useCallback(() => {
		if (!userData?.details) return;

		const details = userData.details as UserDetails;

		setModalAnswers({
			"name-location": {
				firstName: details.firstName || "",
				lastName: details.lastName || "",
				countryCity: details.countryCity || "",
			},
			"birth-year-gender": {
				yearOfBirth: details.yearOfBirth || "",
				gender: details.gender || "",
			},
			"current-status": {
				fertilityStatus: details.fertilityStatus || "",
			},
			"fertility-role": {
				fertilityRole: details.fertilityRole || "",
			},
			"journey-type": {
				fertilityJourneyTypes: details.fertilityJourneyTypes || [],
			},
			"additional-medical-info": {
				additionalInformation: details.additionalInformation || "",
			},
			clinic: {
				hasClinicProvider: details.hasClinicProvider || false,
				providerName: details.providerName || "",
			},
			"change-password": {
				currentPassword: "",
				newPassword: "",
				confirmPassword: "",
				passwordsMatch: false,
				isValidPassword: false,
				accountType:
					userData &&
					"auth0Id" in userData &&
					typeof userData.auth0Id === "string" &&
					userData.auth0Id?.startsWith("auth0|")
						? "local"
						: "google",
			},
		});
	}, [userData]);

	// Generic input change handler
	const handleInputChange = useCallback(
		(modalId: string, field: string, value: unknown) => {
			setModalAnswers((prev) => ({
				...prev,
				[modalId]: {
					...(prev[modalId as keyof ModalAnswers] || {}),
					[field]: value,
				},
			}));
		},
		[],
	);

	// Generic save handler
	const handleSave = useCallback(
		async (modalId: string) => {
			const answers = modalAnswers[modalId as keyof ModalAnswers] || {};

			// Handle password change differently (just console.log for now)
			if (modalId === "change-password") {
				console.log("Password change requested");
				logEvent("profile_password_change_attempted", undefined, {
					account_type:
						userData &&
						"auth0Id" in userData &&
						typeof userData.auth0Id === "string" &&
						userData.auth0Id?.startsWith("auth0|")
							? "local"
							: "google",
				});
				toast.success(tCommon("toast.success.passwordChangeComingSoon"));
				setOpenModalId(null);
				return;
			}

			// Handle other profile fields
			try {
				setLoading(true);

				logEvent("profile_section_save_started", undefined, {
					section: modalId,
				});

				await saveProfileFields(answers);

				if (userData?.details) {
					updateUserData({
						details: {
							...userData.details,
							...answers,
						},
					});
				}

				logEvent("profile_section_saved", undefined, {
					section: modalId,
					fields_count: Object.keys(answers).length,
				});

				toast.success(t("messages.sectionUpdated"));
				setOpenModalId(null);
			} catch (error) {
				console.error("Error saving profile section:", error);
				logEvent("profile_section_save_failed", undefined, {
					section: modalId,
					error: error instanceof Error ? error.message : "Unknown error",
				});
				toast.error(t("messages.failedToSave"));
			} finally {
				setLoading(false);
			}
		},
		[modalAnswers, userData, updateUserData, setLoading, t, logEvent, tCommon],
	);

	// Modal control functions
	const openModal = useCallback(
		(modalId: string) => {
			logEvent("profile_section_opened", undefined, {
				section: modalId,
			});
			setOpenModalId(modalId);
		},
		[logEvent],
	);

	const closeModal = useCallback(() => {
		if (openModalId) {
			logEvent("profile_section_closed", undefined, {
				section: openModalId,
			});
		}
		setOpenModalId(null);
	}, [openModalId, logEvent]);

	return {
		openModalId,
		modalAnswers,
		openModal,
		closeModal,
		handleInputChange,
		handleSave,
		initializeAnswers,
	};
}
